import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Share2, X, AlertTriangle } from "lucide-react";
import { usePitchDeckSharing } from "@/hooks/usePitchDeckSharing";
import { toast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface PitchDeckShareDialogProps {
  pitchDeckId: string;
  shareToken?: string | null;
  shareExpiresAt?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PitchDeckShareDialog = ({
  pitchDeckId,
  shareToken,
  shareExpiresAt,
  open,
  onOpenChange,
}: PitchDeckShareDialogProps) => {
  const { generateShareToken, revokeShareToken } = usePitchDeckSharing();
  // Shared decks embed personal contact details, so default to a 30-day expiry.
  // "Never expires" is an explicit opt-in.
  const [expiresIn, setExpiresIn] = useState("30");

  const shareUrl = shareToken
    ? `${window.location.origin}/share/${shareToken}`
    : null;

  const neverExpires = expiresIn === "never";

  const handleGenerateLink = () => {
    generateShareToken.mutate({
      pitchDeckId,
      expiresInDays: neverExpires ? undefined : parseInt(expiresIn),
      neverExpires,
    });
  };

  const handleCopyLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      toast({
        title: "Link copied",
        description: "Share link copied to clipboard",
      });
    }
  };

  const handleRevokeLink = () => {
    revokeShareToken.mutate(pitchDeckId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share Pitch Deck
          </DialogTitle>
          <DialogDescription>
            {shareToken
              ? "Anyone with this link can view your pitch deck"
              : "Generate a shareable link for this pitch deck"}
          </DialogDescription>
        </DialogHeader>

        {!shareToken ? (
          <div className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Anyone with the share link will be able to view and download your pitch deck.
                Only share with trusted parties.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="expiration">Link Expiration</Label>
              <Select value={expiresIn} onValueChange={setExpiresIn}>
                <SelectTrigger id="expiration">
                  <SelectValue placeholder="Select expiration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days (recommended)</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="never">Never expires</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {neverExpires && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  This link will stay active forever until you revoke it. Your pitch
                  deck includes your name, address, email and phone number.
                </AlertDescription>
              </Alert>
            )}

            <Button
              onClick={handleGenerateLink}
              disabled={generateShareToken.isPending}
              className="w-full"
            >
              <Share2 className="mr-2 h-4 w-4" />
              {generateShareToken.isPending ? "Generating..." : "Generate Share Link"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="share-link">Share Link</Label>
              <div className="flex gap-2">
                <Input
                  id="share-link"
                  value={shareUrl || ""}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button variant="outline" size="icon" onClick={handleCopyLink}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {shareExpiresAt
                  ? `Expires ${new Date(shareExpiresAt).toLocaleDateString()}`
                  : "This link never expires. Revoke it below when you no longer need it."}
              </p>
            </div>

            <div className="pt-2 border-t">
              <Button
                variant="destructive"
                onClick={handleRevokeLink}
                disabled={revokeShareToken.isPending}
                className="w-full"
              >
                <X className="mr-2 h-4 w-4" />
                {revokeShareToken.isPending ? "Revoking..." : "Revoke Share Link"}
              </Button>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Revoking will immediately disable the share link
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
